/**
 * Service-booking list + its pay-balance and review dialogs, shared by the classic My Orders page and the Blinkit
 * one (Bookings tab). Moved verbatim out of OrderHistoryPage.jsx; state and actions come from useOrdersController.
 */
import { Link } from "react-router-dom";
import { Star, Calendar, MapPin, Phone, Loader2, Navigation } from "lucide-react";
import { inr } from "../../../lib/api";
import BookingLocationMap from "../../../components/BookingLocationMap";
import CallButton from "../../../components/CallButton";
import { openMap } from "../../../lib/openMap";

const BOOKING_PILL = {
    PENDING: "bg-amber-100 text-amber-800",
    CONFIRMED: "bg-emerald-100 text-emerald-800",
    COMPLETED: "bg-blue-100 text-blue-800",
    CANCELLED: "bg-red-100 text-red-700",
};

export function BookingsList({ bookings, cancelBooking, openPay, openReview }) {
    return (
        bookings === null ? (
            <div className="p-6 flex items-center justify-center text-muted-foreground"><Loader2 size={18} className="animate-spin" /></div>
        ) : bookings.length === 0 ? (
            <EmptyState testid="bookings-empty" title="No bookings yet" cta="Browse services" ctaTo="/categories" />
        ) : (
            <ul className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="bookings-list">
                {bookings.map((b) => {
                    const addr = renderAddress(b.address);
                    // Once a booking is COMPLETED or CANCELLED the transaction is
                    // closed — ``advance_paid`` intentionally never gets bumped up
                    // to match ``amount_total`` post-completion (it's frozen for the
                    // tenant↔merchant settlement math in collect-cash), so computing
                    // "due" from those two fields alone would show a stale non-zero
                    // balance forever. Treat settled/cancelled bookings as due=0.
                    const isSettled = b.status === "COMPLETED" || b.status === "CANCELLED";
                    const remaining = isSettled ? 0 : Math.max(0, (b.amount_total || 0) - (b.advance_paid || 0));
                    const canPay = (b.status === "PENDING" || b.status === "CONFIRMED") && remaining > 0;
                    return (
                        <li key={b.id} data-testid={`booking-row-${b.id}`} className="bg-card border border-border rounded-2xl p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <h3 className="font-bold line-clamp-1">{b.product_name}</h3>
                                    <p className="text-xs text-muted-foreground mt-0.5">{b.duration_min} min</p>
                                </div>
                                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full ${BOOKING_PILL[b.status] || "bg-muted"}`}>{b.status}</span>
                            </div>
                            <div className="mt-3 text-sm space-y-1">
                                <div className="flex items-center gap-2"><Calendar size={14} className="text-muted-foreground" /> {new Date(b.scheduled_at).toLocaleString()}</div>
                                {addr && <div className="flex items-start gap-2"><MapPin size={14} className="text-muted-foreground mt-0.5" /> <span className="text-muted-foreground">{addr}</span></div>}
                                <BookingLocationMap
                                    lat={b.address?.lat}
                                    lng={b.address?.lng}
                                    label={b.product_name}
                                    className="mt-1"
                                    testid={`booking-map-${b.id}`}
                                />
                                {b.restaurant_name && (
                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                        <span className="text-xs font-bold">{b.restaurant_name}</span>
                                        {b.restaurant_mobile && (
                                            <CallButton
                                                phone={b.restaurant_mobile}
                                                testid={`booking-call-merchant-${b.id}`}
                                                className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-full"
                                            ><Phone size={12} /> Call merchant</CallButton>
                                        )}
                                        {/* Navigate-to-shop only makes sense for AT_SHOP
                                            bookings — the customer is the one travelling.
                                            AT_CUSTOMER bookings store the customer's own
                                            address on ``b.address`` (the merchant travels
                                            to them), so there's nothing useful to navigate to. */}
                                        {b.location_type === "AT_SHOP" && (b.address?.lat != null) && (
                                            <button
                                                type="button"
                                                data-testid={`booking-navigate-shop-${b.id}`}
                                                onClick={() => openMap({
                                                    lat: b.address.lat, lng: b.address.lng,
                                                    label: b.restaurant_name,
                                                })}
                                                className="inline-flex items-center gap-1 text-xs font-bold text-sky-700 dark:text-sky-400 bg-sky-50 dark:bg-sky-950/40 px-2 py-0.5 rounded-full"
                                            ><Navigation size={12} /> Navigate to shop</button>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="mt-3 flex items-center justify-between gap-2">
                                <span className="font-bold text-sm inline-flex items-center gap-0.5">
                                    {inr(b.amount_total)}
                                    <span className="text-xs text-muted-foreground"> · paid {inr(b.advance_paid)}</span>
                                    {remaining > 0 && (
                                        <span data-testid={`booking-remaining-${b.id}`} className="ml-1 text-[11px] font-bold text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 px-2 py-0.5 rounded-full">
                                            {inr(remaining)} due
                                        </span>
                                    )}
                                </span>
                                <div className="flex items-center gap-2 shrink-0">
                                    {canPay && (
                                        <button
                                            onClick={() => openPay(b)}
                                            data-testid={`booking-pay-balance-${b.id}`}
                                            className="text-xs font-bold text-primary-foreground bg-primary rounded-full px-3 py-1"
                                        >Pay balance</button>
                                    )}
                                    {b.status === "COMPLETED" && (
                                        <button
                                            onClick={() => openReview(b)}
                                            data-testid={`booking-rate-${b.id}`}
                                            className="text-[11px] font-bold text-amber-700 dark:text-amber-300 inline-flex items-center gap-1"
                                        >
                                            <Star size={12} fill="currentColor" /> Rate &amp; review
                                        </button>
                                    )}
                                    {(b.status === "PENDING" || b.status === "CONFIRMED") && (
                                        <button onClick={() => cancelBooking(b)} data-testid={`booking-cancel-${b.id}`} className="text-xs font-bold text-red-600">Cancel</button>
                                    )}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
        )
    );
}

export function BookingModals({ paying, setPaying, submitPay, reviewing, setReviewing, submitReview }) {
    return (
        <>
        {/* iter133 — Pay-balance modal */}
        {paying && (() => {
            const b = paying.booking;
            const remaining = Math.max(0, (b.amount_total || 0) - (b.advance_paid || 0));
            return (
                <div
                    data-testid="pay-balance-modal"
                    className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
                    onClick={() => setPaying(null)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="bg-card w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl border border-border p-5 space-y-4"
                    >
                        <div>
                            <h3 className="font-display font-bold text-lg">Pay balance</h3>
                            <p className="text-xs text-muted-foreground mt-1">
                                {b.product_name} · {b.restaurant_name || ""}
                            </p>
                        </div>
                        <div className="rounded-xl bg-muted/50 p-3 text-sm">
                            <Line k="Booking total" v={inr(b.amount_total)} />
                            <Line k="Already paid" v={inr(b.advance_paid)} />
                            <div className="border-t border-border my-2" />
                            <Line k="Remaining" v={<strong className="text-amber-700 dark:text-amber-400">{inr(remaining)}</strong>} />
                        </div>
                        <label className="block text-xs font-bold text-muted-foreground">
                            Amount to pay now
                            <input
                                type="number"
                                min="1"
                                max={remaining}
                                step="1"
                                data-testid="pay-balance-amount"
                                value={paying.amount}
                                onChange={(e) => setPaying({ ...paying, amount: e.target.value })}
                                className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-base text-foreground font-normal"
                                placeholder={String(remaining)}
                            />
                            <span className="mt-1 block text-[11px] text-muted-foreground font-normal">
                                Tip: you can pay less than the remaining amount if you&apos;d like to split payments — the balance stays open until you settle it.
                            </span>
                        </label>
                        <div className="flex gap-2">
                            <button
                                onClick={() => submitPay(remaining)}
                                data-testid="pay-balance-full"
                                className="flex-1 rounded-xl border border-border py-2 text-xs font-bold"
                            >Pay full ({inr(remaining)})</button>
                            <button
                                onClick={() => submitPay()}
                                data-testid="pay-balance-submit"
                                className="flex-1 rounded-xl bg-primary text-primary-foreground py-2 text-sm font-bold"
                            >Pay now</button>
                        </div>
                        <button
                            onClick={() => setPaying(null)}
                            data-testid="pay-balance-cancel"
                            className="w-full text-center text-xs text-muted-foreground pt-1"
                        >Cancel</button>
                    </div>
                </div>
            );
        })()}

        {/* Booking review modal — same interaction shape as the
            pay-balance modal above. */}
        {reviewing && (
            <div
                data-testid="booking-review-modal"
                className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
                onClick={() => setReviewing(null)}
            >
                <div
                    onClick={(e) => e.stopPropagation()}
                    className="bg-card w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl border border-border p-5 space-y-4"
                >
                    <div>
                        <h3 className="font-display font-bold text-lg">Rate this booking</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                            {reviewing.booking.product_name} · {reviewing.booking.restaurant_name || ""}
                        </p>
                    </div>
                    {reviewing.loading ? (
                        <div className="flex justify-center py-4"><Loader2 size={18} className="animate-spin text-muted-foreground" /></div>
                    ) : (
                        <>
                            <div className="flex items-center justify-center gap-1" data-testid="booking-review-stars">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        onClick={() => setReviewing({ ...reviewing, rating: n })}
                                        data-testid={`booking-review-star-${n}`}
                                        aria-label={`${n} star${n > 1 ? "s" : ""}`}
                                    >
                                        <Star
                                            size={32}
                                            className={n <= reviewing.rating ? "fill-amber-500 text-amber-500" : "text-muted-foreground/40"}
                                        />
                                    </button>
                                ))}
                            </div>
                            <label className="block text-xs font-bold text-muted-foreground">
                                Comment (optional)
                                <textarea
                                    rows={3}
                                    data-testid="booking-review-comment"
                                    value={reviewing.comment}
                                    onChange={(e) => setReviewing({ ...reviewing, comment: e.target.value })}
                                    placeholder="How was the service?"
                                    className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm text-foreground font-normal resize-none"
                                />
                            </label>
                            <button
                                onClick={submitReview}
                                data-testid="booking-review-submit"
                                className="w-full rounded-xl bg-primary text-primary-foreground py-2 text-sm font-bold"
                            >Submit review</button>
                        </>
                    )}
                    <button
                        onClick={() => setReviewing(null)}
                        data-testid="booking-review-cancel"
                        className="w-full text-center text-xs text-muted-foreground pt-1"
                    >Cancel</button>
                </div>
            </div>
        )}
        </>
    );
}

export function EmptyState({ testid, title, cta, ctaTo }) {
    return (
        <div className="text-center py-20 px-6" data-testid={testid}>
            <p className="text-sm text-muted-foreground">{title}</p>
            <Link to={ctaTo} className="inline-block mt-3 text-sm font-bold text-primary">{cta} →</Link>
        </div>
    );
}

function Line({ k, v }) {
    return (
        <div className="flex items-center justify-between text-xs py-0.5">
            <span className="text-muted-foreground">{k}</span>
            <span>{v}</span>
        </div>
    );
}

// Bookings store ``address`` as a structured object (new clients) OR
// as a free-text string (legacy). Render whichever shape is present.
function renderAddress(addr) {
    if (!addr) return null;
    if (typeof addr === "string") return addr;
    const parts = [addr.line1, addr.line2, addr.city, addr.pincode].filter(Boolean);
    return parts.length ? parts.join(", ") : null;
}
